import type { ApiEnvironment } from 'src/shared/utils/environment';

import type { PartnerCustomizationCategory } from '../customizationCategories';

import { DesignerPartner } from './configuration';
import { PALETTE_THUMBNAILS_PELLA } from './palettes';

export interface DesignerPartnerProductConfiguration {
  category: PartnerCustomizationCategory;
  productCatalogCategory: string;
  paletteThumbnails?: Record<string, string>;
}

/**
 * Default product catalog categories for the products that populate the edit
 * experience. These are what we populate the edit experience with when there is
 * no associated partner. When there is an associated partner, they populate any
 * category that isn't defined for the partner.
 */
const DEFAULT_PARTNER_PRODUCTS: DesignerPartnerProductConfiguration[] = [
  {
    // NOTE: we currently treat all paint sub-regions as the same product
    // category. See `isCategoryEqual` in `components/Customize/menu.ts`
    category: { type: 'paint', region: 'walls' },
    productCatalogCategory: '820cc7d0-3f37-44d6-a78a-538863c7a84a',
  },
  {
    category: { type: 'material', region: 'walls' },
    productCatalogCategory: 'fa089def-cc9d-4f0f-a98f-743f1a90ee28',
  },
  {
    category: { type: 'material', region: 'roof' },
    productCatalogCategory: 'b7674473-9564-4768-abe4-222c3d8138dc',
  },
  {
    category: { type: 'material', region: 'floor' },
    productCatalogCategory: 'ff3f43ec-edf7-4e60-af40-fb20c322323f',
  },
  {
    category: { type: 'material', region: 'cabinet' },
    productCatalogCategory: '4dbbd4a1-a1fc-4ab4-b2f3-361346d3b1c9',
  },
  {
    category: { type: 'material', region: 'windows' },
    productCatalogCategory: '01e7ba61-ee7c-4ff6-8af4-fe17e59894ce',
  },
  {
    category: { type: 'material', region: 'garage_door' },
    productCatalogCategory: '3171e155-a0de-48c8-902c-d90df942f4e7',
  },
];

/**
 * Partner-specific product catalog categories and configuration. This controls
 * when and how partner products are displayed in the edit exprience in place of
 * the default products.
 */
export const PARTNER_PRODUCT_MAP = {
  [DesignerPartner.YES_COLOURS]: [
    {
      // NOTE: we currently treat all paint sub-regions as the same product
      // category. See `isCategoryEqual` in `components/Customize/menu.ts`
      category: { type: 'paint', region: 'walls' },
      productCatalogCategory: 'ef507d71-2c77-4508-860f-fbefb61d8a25',
    },
  ],
  [DesignerPartner.PELLA]: [
    {
      category: {
        type: 'material',
        region: 'windows',
        paletteFacet: 'color_group',
        primaryLabelFacet: 'grille_pattern',
        secondaryLabelFacet: 'window_type',
        i18nKeyPrimarySecondaryLabel: 'customize.primarySecondaryProductLabel',
      },
      productCatalogCategory: '83223cdc-fe47-4f7a-8e52-20a7bc6ff2d0',
      paletteThumbnails: PALETTE_THUMBNAILS_PELLA,
    },
  ],
  [DesignerPartner.REALTOR]: DEFAULT_PARTNER_PRODUCTS,
  [DesignerPartner.NONE]: DEFAULT_PARTNER_PRODUCTS,
} as const satisfies Record<
  DesignerPartner,
  DesignerPartnerProductConfiguration[]
>;

export type CalloutStyleMap = Partial<Record<string, string[]>>;
export type PartnerCalloutConfiguration = Partial<
  Record<ApiEnvironment, CalloutStyleMap | null>
>;

/**
 * Partner-specific product mappings for style generation entries. These are
 * Product Catalog variation UUIDs mapped to the different styles used in the
 * style generation list. Because we're hard-coding Product Catalog UUIDs, we
 * need to provide a different mapping for each supported API environment.
 *
 * @remarks this structure should always be typed with nested `Partial` objects
 * to force us to handle the empty cases when indexing into the various levels
 * of this mapping.
 */
export const PARTNER_CALLOUT_MAP: Partial<
  Record<DesignerPartner, PartnerCalloutConfiguration>
> = {
  [DesignerPartner.PELLA]: {
    production: {
      Contemporary: [
        '45cfb3d7-f50f-4331-8cf8-be380531cfc1',
        '560b2432-8d48-4ca9-900a-9787d0bbf5db',
        '19b8c85e-23be-4bcd-a383-df9df0595e14',
        'e85fe935-c92e-4a96-a4f6-c623467e6dc8',
        '220853df-e9ca-464d-8369-5c05225c450e',
        'ca42fb41-742f-41e8-b655-27baddaa7050',
        '430b0b7f-8ddd-4ca5-8c0f-0b942abb8dc2',
        '4293660e-30b1-4d02-84b6-fe189e8b047b',
        'f49cb607-777f-493a-88df-1b93f6c569c9',
        '80face67-5c2a-4d4c-87b2-034c5bbe810c',
        '7604329d-6d1a-4dea-9bd6-dc92306fcec2',
        'd97288e1-89f7-40e2-ba6e-688778f787ec',
      ],
      'Mid-century Modern': [
        '45cfb3d7-f50f-4331-8cf8-be380531cfc1',
        'e8ca4c51-f3c3-4dfd-9f82-1bfa466b22ce',
        '560b2432-8d48-4ca9-900a-9787d0bbf5db',
        'de9a605e-ce8a-40b1-8812-0d9aec97971d',
        'c8f8aca8-14e5-455f-a64d-577df18f2cd6',
        '19b8c85e-23be-4bcd-a383-df9df0595e14',
        'e85fe935-c92e-4a96-a4f6-c623467e6dc8',
        '220853df-e9ca-464d-8369-5c05225c450e',
        'bb6108c5-26f5-48fc-9ff9-348b1c52935a',
        'ca42fb41-742f-41e8-b655-27baddaa7050',
        '430b0b7f-8ddd-4ca5-8c0f-0b942abb8dc2',
        'b6c98e2b-b8b2-4e5a-ba8e-9a82a4bba84d',
        '99e9e435-7559-460d-aefa-aefc2821534f',
        'f49cb607-777f-493a-88df-1b93f6c569c9',
        '7a782012-c14f-44e4-97ae-3811ac2b3ad0',
        '7604329d-6d1a-4dea-9bd6-dc92306fcec2',
        'd97288e1-89f7-40e2-ba6e-688778f787ec',
        '72bdf833-9112-44ed-bd18-1f3cc025718b',
        '6ba7a9a9-3ee9-4795-873f-950c9e605142',
      ],
      Transitional: [
        '45cfb3d7-f50f-4331-8cf8-be380531cfc1',
        '3c98164d-2a92-4c44-9fa3-4a868e1da6ab',
        'b42340d7-ae4e-4ed7-ae63-0b6e7ad9c6c4',
        '3164a594-0380-4056-a126-96d0184163cf',
        'e8ca4c51-f3c3-4dfd-9f82-1bfa466b22ce',
        '560b2432-8d48-4ca9-900a-9787d0bbf5db',
        'f09ac4bf-6408-48e5-8adf-e1295b5276b3',
        '42f356ed-42eb-4dbe-9d79-fb83d8a9899a',
        'c5c9d81f-d022-454e-bbd5-010913df3a4e',
        'fa584ecc-3c2a-4d60-9049-f9e9e303f4bd',
        'c10d64ae-4e18-407f-87de-76442f584d3e',
        'de9a605e-ce8a-40b1-8812-0d9aec97971d',
        'ff337be6-5fd3-4bbf-baaa-bd55f87c9958',
        '91d2c9f0-9689-4fc9-95d2-69983e82a8af',
        '9ae2b9ab-724d-4e1d-a2a7-b6c361f86eaa',
        'c8f8aca8-14e5-455f-a64d-577df18f2cd6',
        '19b8c85e-23be-4bcd-a383-df9df0595e14',
        '248b8420-66cd-499d-9041-f072266aa60f',
        'a6f4b204-9486-478b-a899-a90179747501',
        '9eb405ca-fa4e-4310-bfc6-1799c2bba1e2',
        '5355b3c5-45e5-453f-823e-658756acff67',
        '3e27ff2b-395e-4e6f-8900-bad55ba419e1',
        'e85fe935-c92e-4a96-a4f6-c623467e6dc8',
        '220853df-e9ca-464d-8369-5c05225c450e',
        'c1a3cce6-0c84-4bc1-a910-af65a46af564',
        'e5fafe3c-5d26-44a1-a835-19cdd82c3a12',
        '167d2fbb-49d6-45a1-9321-289e255a180e',
        'bb6108c5-26f5-48fc-9ff9-348b1c52935a',
        'ca42fb41-742f-41e8-b655-27baddaa7050',
        'dfa859ee-b459-485e-b934-bd16345c59e9',
        '66b14dc8-dd8a-4627-9465-d2229769faff',
        'e267dda3-e1ec-4a7d-8611-46e21757d141',
        'e14ad37d-eece-4cd4-9649-4315f7b4305d',
        'd72e4f40-88bd-42f3-9415-193009928376',
        '41192e98-a271-46da-8e09-dd176058f546',
        '430b0b7f-8ddd-4ca5-8c0f-0b942abb8dc2',
        'b76c5c48-3536-4305-b7a9-f0fee3463e20',
        '35c50d41-2ad9-4cc7-aae0-69cfba4f5504',
        'd36bca4d-d641-4f94-914e-24b9d88eb045',
        '35daadf2-a7a7-4a18-b11e-b1c01dccc42f',
        'b6c98e2b-b8b2-4e5a-ba8e-9a82a4bba84d',
        '7665a066-2ba1-47c9-88ce-505b8e37bcc9',
        'bc4eb74b-3b24-4cdb-984e-867c9e5d2962',
        '600b72c9-3d44-42ba-88a4-9120ddb79a52',
        '78a67e4b-559f-4e4b-9739-62b053dfc358',
        '2ec2acca-2d83-4fb9-823f-e4568c7fa429',
        '146473fa-86c8-4cc9-9265-501423ce6fcb',
        '4293660e-30b1-4d02-84b6-fe189e8b047b',
        '385317e9-7d17-46c0-82b3-698ab52c6eef',
        '905c3d01-e646-48f4-8dc0-3f9f039d61f5',
        '548fe177-65ea-4e61-bc2a-71aa0cb04197',
        '718a603c-df83-496d-864c-cb5aef1423c0',
        '99e9e435-7559-460d-aefa-aefc2821534f',
        'f49cb607-777f-493a-88df-1b93f6c569c9',
        'ca3a399a-5d1d-406e-a585-3dd93e27fd54',
        'f310f09b-fdd7-4b9d-b854-a8a42d5faade',
        '1f4ea798-04a8-45ef-8940-9229601b9a2f',
        'a197e64c-ad79-4fc1-9c09-b5c790460174',
        'bcb6f1dc-7e90-4443-8a1c-057fdf622ecc',
        '80face67-5c2a-4d4c-87b2-034c5bbe810c',
        '49edda54-50f7-4938-a4a0-711ae87ad066',
        '5af4b63d-345f-46e0-930c-74cd9f224488',
        'ee85f1bd-1e19-4356-ada3-a2bdd1e59697',
        '6085238d-a43d-43a1-9286-fcfefaebfb49',
        '7a782012-c14f-44e4-97ae-3811ac2b3ad0',
        '7604329d-6d1a-4dea-9bd6-dc92306fcec2',
        '8d17e517-51d5-4059-8567-e79a6762c07c',
        '7e0a1b16-758a-434e-9577-2e656c8f2f4a',
        'c8d9a52d-6cde-4e91-a035-37aeb660eab2',
        'e82bacf0-5194-4309-b306-8e3b07156f6e',
        '7075e8bc-8e86-4c70-9256-b5274f34dbf5',
        '3e64695a-6eef-4a2e-b4ee-607365d02049',
        '4e238dbe-ee30-410b-9328-f87876a68179',
        'fdc28ec1-16e0-4b5e-93bd-068689b54567',
        'ea7cf31c-4975-4fd2-b231-8af443cb1732',
        'f531b8a4-3ea1-421d-a954-bdbcdae85a3d',
        '2d803168-defd-48f3-8abb-695eb3f99bfd',
        '7e58ef48-1a80-40cf-9c1a-1b8226680855',
        '115b553c-c0fd-478c-9a27-144d6070802c',
        '1ad9f029-5cbe-44b0-bb29-0cb4e960e05f',
        '65ecd102-ef0b-41ef-a53d-0e393428e812',
        'b93d3eab-268a-4efd-8a4f-ae0e4d169e3d',
        '9344bc73-7cd7-4b3a-8fe4-05d7d75b59da',
        '886c8f28-9096-4cc9-a854-c576ab7613e0',
        'f1679811-aa45-4d2a-bab8-ccceea598eff',
        '75684376-38c7-473a-b240-5c4283408117',
        '5090f188-9f8a-465c-a188-2530f65dd784',
        '51fcf1a3-44fa-4f02-8674-199d60db556b',
        '4d2eb4e5-c6c3-488f-bc83-a6a4f660328e',
        '5102026d-6181-46a3-98b9-bbf4fbeb31b5',
        '78be3d4f-64a6-4100-aebc-c560d4eec8e9',
        'a2539e6a-5f09-4cd6-ab33-ebc6076b9d3b',
        '28a8d893-6121-462e-bb4f-2eed2ef17461',
        '25c406e6-46b5-4fd3-8411-ac2ae699906d',
        'c1675cdb-a5ed-4284-b2cd-0341e3349e7e',
        '82be6781-0222-4dd2-bdee-7b8c2744c215',
        'fa7487ae-f0ce-4e97-99ed-deca68a7ac34',
        'aa25cf63-aa19-4ade-af15-9169e62512ba',
        'edb2f1eb-4477-403e-b560-82a0d78f6e41',
        '44d32457-07ec-4e37-972b-70a0fa6bbe27',
        '5b579ebb-7ec3-436c-9f43-3cbf7b28d76c',
        '2330984a-360e-48de-976f-4e1ea508b4c7',
        '069325e6-46ff-4f3c-9b79-e909929f1d44',
        'fca9773e-b6b8-42d2-9815-b05530b9775e',
        '54c7810b-f40f-44bf-8bb9-7074c6cc2687',
        '7aeeae83-fda8-437e-8e5d-cf0bb509c84a',
        'e5f4e9d5-15b4-449c-8fa4-19197512df7b',
        'd97288e1-89f7-40e2-ba6e-688778f787ec',
        'de95d137-4346-44f2-9d95-1c76d8d00aca',
        '72bdf833-9112-44ed-bd18-1f3cc025718b',
        '6ba7a9a9-3ee9-4795-873f-950c9e605142',
        '49a63ec2-77f7-4252-912d-c49279d91963',
        '7e669dd2-55f1-4c8d-b7a9-09bb2c186ade',
        'b8854540-8139-4b22-a9ea-8d6f7dde0898',
        '9a43c201-abb0-4a64-b5e3-a22f344bf2de',
        'b679ce3a-6d4f-44e8-ae24-00bd604a0124',
        'e8e0fd23-6e26-4bcd-a54e-c0ba9dfe7736',
        'c656667b-2b37-48b9-886d-906038a3141f',
        '85d66c96-a683-4d39-a5fe-4d0f9c833ee0',
      ],
      'French European': [
        '45cfb3d7-f50f-4331-8cf8-be380531cfc1',
        '3c98164d-2a92-4c44-9fa3-4a868e1da6ab',
        'b42340d7-ae4e-4ed7-ae63-0b6e7ad9c6c4',
        '3164a594-0380-4056-a126-96d0184163cf',
        'f09ac4bf-6408-48e5-8adf-e1295b5276b3',
        '42f356ed-42eb-4dbe-9d79-fb83d8a9899a',
        'c5c9d81f-d022-454e-bbd5-010913df3a4e',
        '8b38d217-b34e-43b3-b075-5f6f23f5f5af',
        'fa584ecc-3c2a-4d60-9049-f9e9e303f4bd',
        'de9a605e-ce8a-40b1-8812-0d9aec97971d',
        'ff337be6-5fd3-4bbf-baaa-bd55f87c9958',
        '91d2c9f0-9689-4fc9-95d2-69983e82a8af',
        '9ae2b9ab-724d-4e1d-a2a7-b6c361f86eaa',
        'a6f4b204-9486-478b-a899-a90179747501',
        '9eb405ca-fa4e-4310-bfc6-1799c2bba1e2',
        '5355b3c5-45e5-453f-823e-658756acff67',
        '3e27ff2b-395e-4e6f-8900-bad55ba419e1',
        '220853df-e9ca-464d-8369-5c05225c450e',
        'c1a3cce6-0c84-4bc1-a910-af65a46af564',
        'e5fafe3c-5d26-44a1-a835-19cdd82c3a12',
        '167d2fbb-49d6-45a1-9321-289e255a180e',
        'dfa859ee-b459-485e-b934-bd16345c59e9',
        '66b14dc8-dd8a-4627-9465-d2229769faff',
        'e267dda3-e1ec-4a7d-8611-46e21757d141',
        'e14ad37d-eece-4cd4-9649-4315f7b4305d',
        'd72e4f40-88bd-42f3-9415-193009928376',
        '41192e98-a271-46da-8e09-dd176058f546',
        'b76c5c48-3536-4305-b7a9-f0fee3463e20',
        '7665a066-2ba1-47c9-88ce-505b8e37bcc9',
        'bc4eb74b-3b24-4cdb-984e-867c9e5d2962',
        '600b72c9-3d44-42ba-88a4-9120ddb79a52',
        '78a67e4b-559f-4e4b-9739-62b053dfc358',
        '2ec2acca-2d83-4fb9-823f-e4568c7fa429',
        '146473fa-86c8-4cc9-9265-501423ce6fcb',
        '99e9e435-7559-460d-aefa-aefc2821534f',
        'f49cb607-777f-493a-88df-1b93f6c569c9',
        '7604329d-6d1a-4dea-9bd6-dc92306fcec2',
        '8d17e517-51d5-4059-8567-e79a6762c07c',
        '7e0a1b16-758a-434e-9577-2e656c8f2f4a',
        'c8d9a52d-6cde-4e91-a035-37aeb660eab2',
        'e82bacf0-5194-4309-b306-8e3b07156f6e',
        '7075e8bc-8e86-4c70-9256-b5274f34dbf5',
        'de95d137-4346-44f2-9d95-1c76d8d00aca',
        '72bdf833-9112-44ed-bd18-1f3cc025718b',
        '6ba7a9a9-3ee9-4795-873f-950c9e605142',
        '49a63ec2-77f7-4252-912d-c49279d91963',
        '7e669dd2-55f1-4c8d-b7a9-09bb2c186ade',
        'b8854540-8139-4b22-a9ea-8d6f7dde0898',
        '9a43c201-abb0-4a64-b5e3-a22f344bf2de',
        'b679ce3a-6d4f-44e8-ae24-00bd604a0124',
        'e8e0fd23-6e26-4bcd-a54e-c0ba9dfe7736',
        'c656667b-2b37-48b9-886d-906038a3141f',
      ],
      Farmhouse: [
        '3c98164d-2a92-4c44-9fa3-4a868e1da6ab',
        '42f356ed-42eb-4dbe-9d79-fb83d8a9899a',
        'c5c9d81f-d022-454e-bbd5-010913df3a4e',
        '8b38d217-b34e-43b3-b075-5f6f23f5f5af',
        'de9a605e-ce8a-40b1-8812-0d9aec97971d',
        'ff337be6-5fd3-4bbf-baaa-bd55f87c9958',
        '91d2c9f0-9689-4fc9-95d2-69983e82a8af',
        '9ae2b9ab-724d-4e1d-a2a7-b6c361f86eaa',
        '248b8420-66cd-499d-9041-f072266aa60f',
        'a6f4b204-9486-478b-a899-a90179747501',
        '9eb405ca-fa4e-4310-bfc6-1799c2bba1e2',
        '5355b3c5-45e5-453f-823e-658756acff67',
        'c1a3cce6-0c84-4bc1-a910-af65a46af564',
        'b76c5c48-3536-4305-b7a9-f0fee3463e20',
        '385317e9-7d17-46c0-82b3-698ab52c6eef',
        '905c3d01-e646-48f4-8dc0-3f9f039d61f5',
        '548fe177-65ea-4e61-bc2a-71aa0cb04197',
        '718a603c-df83-496d-864c-cb5aef1423c0',
        'ca3a399a-5d1d-406e-a585-3dd93e27fd54',
        'f310f09b-fdd7-4b9d-b854-a8a42d5faade',
        '1f4ea798-04a8-45ef-8940-9229601b9a2f',
        'a197e64c-ad79-4fc1-9c09-b5c790460174',
        'bcb6f1dc-7e90-4443-8a1c-057fdf622ecc',
        '49edda54-50f7-4938-a4a0-711ae87ad066',
        '5af4b63d-345f-46e0-930c-74cd9f224488',
        'ee85f1bd-1e19-4356-ada3-a2bdd1e59697',
        '6085238d-a43d-43a1-9286-fcfefaebfb49',
        '8d17e517-51d5-4059-8567-e79a6762c07c',
        '7e0a1b16-758a-434e-9577-2e656c8f2f4a',
        'c8d9a52d-6cde-4e91-a035-37aeb660eab2',
        'e82bacf0-5194-4309-b306-8e3b07156f6e',
        '7075e8bc-8e86-4c70-9256-b5274f34dbf5',
        '3e64695a-6eef-4a2e-b4ee-607365d02049',
        '4e238dbe-ee30-410b-9328-f87876a68179',
        'fdc28ec1-16e0-4b5e-93bd-068689b54567',
        'ea7cf31c-4975-4fd2-b231-8af443cb1732',
        'f531b8a4-3ea1-421d-a954-bdbcdae85a3d',
        '2d803168-defd-48f3-8abb-695eb3f99bfd',
        '7e58ef48-1a80-40cf-9c1a-1b8226680855',
        '115b553c-c0fd-478c-9a27-144d6070802c',
        '1ad9f029-5cbe-44b0-bb29-0cb4e960e05f',
        '65ecd102-ef0b-41ef-a53d-0e393428e812',
        'b93d3eab-268a-4efd-8a4f-ae0e4d169e3d',
        '9344bc73-7cd7-4b3a-8fe4-05d7d75b59da',
        '886c8f28-9096-4cc9-a854-c576ab7613e0',
        'f1679811-aa45-4d2a-bab8-ccceea598eff',
        '75684376-38c7-473a-b240-5c4283408117',
        '5090f188-9f8a-465c-a188-2530f65dd784',
        '51fcf1a3-44fa-4f02-8674-199d60db556b',
        '4d2eb4e5-c6c3-488f-bc83-a6a4f660328e',
        '5102026d-6181-46a3-98b9-bbf4fbeb31b5',
        '78be3d4f-64a6-4100-aebc-c560d4eec8e9',
        'a2539e6a-5f09-4cd6-ab33-ebc6076b9d3b',
        '28a8d893-6121-462e-bb4f-2eed2ef17461',
        '25c406e6-46b5-4fd3-8411-ac2ae699906d',
        'c1675cdb-a5ed-4284-b2cd-0341e3349e7e',
        '82be6781-0222-4dd2-bdee-7b8c2744c215',
        'fa7487ae-f0ce-4e97-99ed-deca68a7ac34',
        'aa25cf63-aa19-4ade-af15-9169e62512ba',
        'edb2f1eb-4477-403e-b560-82a0d78f6e41',
        '44d32457-07ec-4e37-972b-70a0fa6bbe27',
        '5b579ebb-7ec3-436c-9f43-3cbf7b28d76c',
        '2330984a-360e-48de-976f-4e1ea508b4c7',
        '069325e6-46ff-4f3c-9b79-e909929f1d44',
        'fca9773e-b6b8-42d2-9815-b05530b9775e',
        '54c7810b-f40f-44bf-8bb9-7074c6cc2687',
        '7aeeae83-fda8-437e-8e5d-cf0bb509c84a',
        'e5f4e9d5-15b4-449c-8fa4-19197512df7b',
        'de95d137-4346-44f2-9d95-1c76d8d00aca',
      ],
      Craftsman: [
        'b42340d7-ae4e-4ed7-ae63-0b6e7ad9c6c4',
        '3164a594-0380-4056-a126-96d0184163cf',
        'e8ca4c51-f3c3-4dfd-9f82-1bfa466b22ce',
        '8b38d217-b34e-43b3-b075-5f6f23f5f5af',
        'fa584ecc-3c2a-4d60-9049-f9e9e303f4bd',
        'c10d64ae-4e18-407f-87de-76442f584d3e',
        'c8f8aca8-14e5-455f-a64d-577df18f2cd6',
        '3e27ff2b-395e-4e6f-8900-bad55ba419e1',
        'e5fafe3c-5d26-44a1-a835-19cdd82c3a12',
        '167d2fbb-49d6-45a1-9321-289e255a180e',
        'bb6108c5-26f5-48fc-9ff9-348b1c52935a',
        'dfa859ee-b459-485e-b934-bd16345c59e9',
        '66b14dc8-dd8a-4627-9465-d2229769faff',
        'e267dda3-e1ec-4a7d-8611-46e21757d141',
        'e14ad37d-eece-4cd4-9649-4315f7b4305d',
        'd72e4f40-88bd-42f3-9415-193009928376',
        '41192e98-a271-46da-8e09-dd176058f546',
        'b6c98e2b-b8b2-4e5a-ba8e-9a82a4bba84d',
        '3e64695a-6eef-4a2e-b4ee-607365d02049',
        '4e238dbe-ee30-410b-9328-f87876a68179',
        'fdc28ec1-16e0-4b5e-93bd-068689b54567',
        'ea7cf31c-4975-4fd2-b231-8af443cb1732',
        'f531b8a4-3ea1-421d-a954-bdbcdae85a3d',
        '2d803168-defd-48f3-8abb-695eb3f99bfd',
        '7e58ef48-1a80-40cf-9c1a-1b8226680855',
        '115b553c-c0fd-478c-9a27-144d6070802c',
        '1ad9f029-5cbe-44b0-bb29-0cb4e960e05f',
        '65ecd102-ef0b-41ef-a53d-0e393428e812',
        'b93d3eab-268a-4efd-8a4f-ae0e4d169e3d',
        '9344bc73-7cd7-4b3a-8fe4-05d7d75b59da',
        '886c8f28-9096-4cc9-a854-c576ab7613e0',
        'f1679811-aa45-4d2a-bab8-ccceea598eff',
        '75684376-38c7-473a-b240-5c4283408117',
        '5090f188-9f8a-465c-a188-2530f65dd784',
        '51fcf1a3-44fa-4f02-8674-199d60db556b',
        '4d2eb4e5-c6c3-488f-bc83-a6a4f660328e',
        '5102026d-6181-46a3-98b9-bbf4fbeb31b5',
        '78be3d4f-64a6-4100-aebc-c560d4eec8e9',
        'a2539e6a-5f09-4cd6-ab33-ebc6076b9d3b',
        '28a8d893-6121-462e-bb4f-2eed2ef17461',
        '25c406e6-46b5-4fd3-8411-ac2ae699906d',
        'c1675cdb-a5ed-4284-b2cd-0341e3349e7e',
        '82be6781-0222-4dd2-bdee-7b8c2744c215',
        'fa7487ae-f0ce-4e97-99ed-deca68a7ac34',
        'aa25cf63-aa19-4ade-af15-9169e62512ba',
        'edb2f1eb-4477-403e-b560-82a0d78f6e41',
        '44d32457-07ec-4e37-972b-70a0fa6bbe27',
        '5b579ebb-7ec3-436c-9f43-3cbf7b28d76c',
        '2330984a-360e-48de-976f-4e1ea508b4c7',
        '069325e6-46ff-4f3c-9b79-e909929f1d44',
        'fca9773e-b6b8-42d2-9815-b05530b9775e',
        '54c7810b-f40f-44bf-8bb9-7074c6cc2687',
        '7aeeae83-fda8-437e-8e5d-cf0bb509c84a',
        'e5f4e9d5-15b4-449c-8fa4-19197512df7b',
        '49a63ec2-77f7-4252-912d-c49279d91963',
      ],
      Monochromatic: [
        '430b0b7f-8ddd-4ca5-8c0f-0b942abb8dc2',
        'b76c5c48-3536-4305-b7a9-f0fee3463e20',
        '35c50d41-2ad9-4cc7-aae0-69cfba4f5504',
        'd36bca4d-d641-4f94-914e-24b9d88eb045',
        '35daadf2-a7a7-4a18-b11e-b1c01dccc42f',
        'b6c98e2b-b8b2-4e5a-ba8e-9a82a4bba84d',
        '7665a066-2ba1-47c9-88ce-505b8e37bcc9',
        'bc4eb74b-3b24-4cdb-984e-867c9e5d2962',
        '600b72c9-3d44-42ba-88a4-9120ddb79a52',
        '78a67e4b-559f-4e4b-9739-62b053dfc358',
        '2ec2acca-2d83-4fb9-823f-e4568c7fa429',
        '146473fa-86c8-4cc9-9265-501423ce6fcb',
        '4293660e-30b1-4d02-84b6-fe189e8b047b',
        '385317e9-7d17-46c0-82b3-698ab52c6eef',
        '905c3d01-e646-48f4-8dc0-3f9f039d61f5',
        '548fe177-65ea-4e61-bc2a-71aa0cb04197',
        '718a603c-df83-496d-864c-cb5aef1423c0',
        'ca3a399a-5d1d-406e-a585-3dd93e27fd54',
        'f310f09b-fdd7-4b9d-b854-a8a42d5faade',
        '1f4ea798-04a8-45ef-8940-9229601b9a2f',
        'a197e64c-ad79-4fc1-9c09-b5c790460174',
        'bcb6f1dc-7e90-4443-8a1c-057fdf622ecc',
        '80face67-5c2a-4d4c-87b2-034c5bbe810c',
        '49edda54-50f7-4938-a4a0-711ae87ad066',
        '5af4b63d-345f-46e0-930c-74cd9f224488',
        'ee85f1bd-1e19-4356-ada3-a2bdd1e59697',
        '6085238d-a43d-43a1-9286-fcfefaebfb49',
        '7a782012-c14f-44e4-97ae-3811ac2b3ad0',
      ],
    },
    staging: {
      Contemporary: [
        '0516bf60-b37d-4285-852a-44568b7b972d',
        '2bb27df8-29ba-4dae-9768-be98c2519728',
        '87f6ed50-e3b8-437e-9cbb-0af2fa596c78',
        'fd529282-8a13-44de-a210-45c3fa28b45c',
        '4a8696e0-4cac-4a79-9849-2f6bd3eef327',
        'ce78daa0-7d63-4b08-b2b9-45afffb4a057',
        'f27859ad-680b-4ef0-a231-890ca45fc0e1',
        'c788dc38-6647-4601-899d-bfdf5c0dcfb9',
        'd7d9b82b-110a-427f-bb40-f9aa76da7d37',
        'a65268e3-ca79-4c80-bdec-c1a7f255b9cf',
        '27ff0bc7-d247-482f-9f3d-d27e05a4090e',
        'de9c1e87-ac9d-4df1-8e8f-88e8b841a5fb',
      ],
      'Mid-century Modern': [
        '0516bf60-b37d-4285-852a-44568b7b972d',
        '2bb27df8-29ba-4dae-9768-be98c2519728',
        'fd529282-8a13-44de-a210-45c3fa28b45c',
        '4a8696e0-4cac-4a79-9849-2f6bd3eef327',
        'ce78daa0-7d63-4b08-b2b9-45afffb4a057',
        'f66be369-4e36-44c2-b8e8-707453bd2839',
        '68170c90-5a0d-4a91-9cf5-b4ab9579b7c5',
        'f27859ad-680b-4ef0-a231-890ca45fc0e1',
        '9cd9e9a2-6410-4a17-a1c4-9c912cfced84',
        'eac88806-28bc-46c4-bb6c-52b573e8520a',
        'b2da217c-4f4e-4750-a7d7-df60cda4e8f2',
        '1271ffc5-5870-4fa1-afe2-7d632a3b9a75',
        'b67c2742-9b76-45d4-ae13-43357f1104a3',
        'd7d9b82b-110a-427f-bb40-f9aa76da7d37',
        'a65268e3-ca79-4c80-bdec-c1a7f255b9cf',
        '4c5c8625-37cb-42e4-8f08-e4bb13173aaf',
        '27ff0bc7-d247-482f-9f3d-d27e05a4090e',
        'ac0c33ec-2fd4-439a-85de-fd0cc3b20353',
        'de9c1e87-ac9d-4df1-8e8f-88e8b841a5fb',
      ],
      Transitional: [
        '79e637e9-896e-4837-92a6-852b9a2454be',
        'afa7576d-7035-406c-8a7d-8e55ce75388c',
        '5c03175e-243a-43f3-bbef-8e230df1f307',
        'f6465c32-634e-4147-9802-a4ba6198e1ee',
        '0c2e8edd-e304-4069-b175-f333f7e9fcfe',
        '4a8696e0-4cac-4a79-9849-2f6bd3eef327',
        'acea2755-6be3-4ffa-8327-a106dc7384c6',
        'bd46877a-cf7d-4e67-a548-5c1028d2b0fb',
        '830c1e0a-a2f0-4419-b5c0-44b8a3cb7be0',
        'f6f61d69-56dd-45d6-a92c-640f916763b8',
        'ce78daa0-7d63-4b08-b2b9-45afffb4a057',
        '2fb48995-1d2a-4926-bea9-5c1e4536ef0b',
        'c70d76fd-1a6e-4f80-9671-bf398b5a15fa',
        '9c063cfb-3ff8-41df-beb5-820f7fd46324',
        '6644b756-d227-47c1-ad79-c1902fe5d68d',
        'cdd058c3-e28e-4f4d-a111-56902c19cbf1',
        'f27859ad-680b-4ef0-a231-890ca45fc0e1',
        'c788dc38-6647-4601-899d-bfdf5c0dcfb9',
        'b67c2742-9b76-45d4-ae13-43357f1104a3',
        '8cbe4d0a-8b68-4b41-8079-ed3fc927f82e',
        '3ac14335-bd2f-4b35-a1e2-7e77ed44c0ce',
        '509886e8-7d5e-4434-a33f-9173b684c918',
        '1271ffc5-5870-4fa1-afe2-7d632a3b9a75',
        'cf7e311d-1365-41a4-aeea-5559322ab20a',
        '44abd449-7add-4e96-a718-970518fffef1',
        'eac88806-28bc-46c4-bb6c-52b573e8520a',
        '565f4a72-1870-4e8c-bbeb-ebdebbc46f16',
        '5665234e-0393-4fa5-babf-02b59759a351',
        'ac0c33ec-2fd4-439a-85de-fd0cc3b20353',
        'f997baf1-1099-4ebf-a550-ad1c098b1791',
        'f603abd9-3be3-4380-b8c1-b958e2ca3897',
        '4c5c8625-37cb-42e4-8f08-e4bb13173aaf',
        '1958be19-7010-4b03-b9ee-3d2b578d2ba8',
        'e12ab406-92e9-4a8c-9d26-dd2a738da01e',
        'f1662617-510b-471d-b971-34d89498343e',
        '0516bf60-b37d-4285-852a-44568b7b972d',
        '80a5f7dd-87bb-48f0-8873-2ceb85639c62',
        'a1d7db01-6a60-4f45-8f42-0cdc9e83a6d1',
        'f822addf-d8ff-4052-8052-393b98867969',
        '0b7ecc32-d8ed-4c27-a732-9851b49a2099',
        '27ff0bc7-d247-482f-9f3d-d27e05a4090e',
        '2bb27df8-29ba-4dae-9768-be98c2519728',
        '13be67b3-dd36-41b9-8577-1c302c5444bf',
        '7c23e6b4-d5b6-45ba-8c04-021259e27d1b',
        'd7d9b82b-110a-427f-bb40-f9aa76da7d37',
        '873b6dc4-e5b5-48cb-99d2-63fa84f5e552',
        '2a84f540-c6a9-46f1-bb2f-44488dceee7c',
        '431036f1-3f52-4ebd-a709-f17942978174',
        '30157ff9-e1d2-4deb-9e9c-34234cb7aec2',
        'fd529282-8a13-44de-a210-45c3fa28b45c',
        '15b85a9e-3661-40c8-96be-beaeb1e8f86c',
        '7f8a7388-c9c6-4ff3-8656-192e1550e885',
        'f300902e-c04d-45f5-8fa0-e83470e9e48d',
        '9974d8bf-7750-4348-a60b-1c9d0ea718d5',
        'fa5550d9-b2c3-42e4-95b1-ff6388e772b5',
        'b2da217c-4f4e-4750-a7d7-df60cda4e8f2',
        '2094df91-2e64-4e5f-b04f-8f387af482fa',
        '18b8e1fa-e6e2-4027-b155-e70766f8f29a',
        '33b53c97-7be8-4a7d-8ee5-439861ad91fe',
        '94a01e22-2690-401b-bf08-2f1c0fb20ad1',
        'd7b4dea4-3957-4d26-b691-9b56c1fe45aa',
        '87f6ed50-e3b8-437e-9cbb-0af2fa596c78',
        'a75d64fb-c6b2-4a0a-be1a-7bd6e2971516',
        '159fddbb-df6c-447b-8ae0-9f9f7a7ee1b0',
        'de9c1e87-ac9d-4df1-8e8f-88e8b841a5fb',
        '5de3ffbd-9245-48d6-93fb-435ca13f8c1f',
        '0f27de8c-c6e6-44f9-98df-01ea137de933',
        'c8105714-4d01-4e5c-8259-36f9f6014550',
        '388330d8-e6f1-4a83-953d-9d9517cbaaa1',
        'b95de443-d59b-421d-8de8-eb45751a7add',
        '80a2a5ad-b09b-4708-8b98-e4e7056192e1',
        '38c8ab72-e815-4d5e-93e6-1fd38943b363',
        '9cd9e9a2-6410-4a17-a1c4-9c912cfced84',
        'eac3c1b7-8525-4cb4-aeeb-33faee03c89c',
        '420402df-226c-4c13-8c45-f44dd6eca6a5',
        '8883d5c2-8a9e-44c8-9831-225d91c84f03',
        '7e127254-e2df-4411-8cc9-ad2c3e27e313',
        '2ddc6fad-830a-4a4d-b666-f49869961146',
        'de37cef7-0903-47b0-89bf-a2ca2397766c',
        'f6db61b1-b3b2-4935-8140-73e38287512f',
        '050c99fd-024e-4bf3-a41f-8103149332ef',
        'f559ec74-6ad9-4069-b5bd-034dccab6c5a',
        '0d31ac35-4a16-45db-a15c-d72721731c11',
        '5148252a-f019-4126-a48d-fc20a03a3dda',
        'de59b393-13f0-4dd8-96f7-59aaed3ae416',
        '014708a7-2149-44f8-abc2-2baa578ebb65',
        '7ec3f9ac-a887-48ff-a509-b1d6ae7cbd2d',
        '5c772a56-433c-473d-82f5-6bef76424438',
        'ed5d55b0-530d-4d88-9e56-4f475ecabd1a',
        '9419cfde-98a8-4d12-b389-f6410a31274b',
        'e24ed235-f1b2-4428-a2de-e4ba530ae0a2',
        'a483b175-e2d2-441b-875e-7bea70d0e7ad',
        '9e156fd8-44d4-41d6-90e9-74487c9a4e3e',
        '68170c90-5a0d-4a91-9cf5-b4ab9579b7c5',
        '311673bb-a6e5-4678-a053-bc5997c0ce3e',
        'f1364ea3-7449-419d-bd63-36344062d0fb',
        '62b71949-d83c-4276-9fa2-43fa44cc3600',
        'b3cb9772-5169-4dab-98d5-f50bd7381e01',
        '63e7c92c-3f5e-4261-81bb-97a091f421d1',
        '8f393846-5f2d-4064-a46d-c2088f225548',
        '95222bc7-e9ad-4e77-af22-0ddf4d4e0db3',
        '3e8771eb-513a-43dd-aa80-3f91b8fc9f31',
        'f66be369-4e36-44c2-b8e8-707453bd2839',
        'a65268e3-ca79-4c80-bdec-c1a7f255b9cf',
        '56e8517b-716e-4d74-8c8c-0140fc2b0a29',
        '1b4ea942-7663-4907-878a-d37ab1809d5b',
        'd72fa511-8b05-463d-8974-503ada9b9e33',
        'df7f1be4-275f-4cc7-8194-461b66f84859',
        '286472f5-0d0e-4402-ba3e-6b7746f0f941',
        'ebc0508b-3c7c-4e78-b5ef-83b2fd990853',
        '40473c4d-b269-4680-9151-bfe12508822c',
        'a9240385-4cc8-4530-afd1-adcbe1d45314',
        'd590f654-c290-4476-93f6-a9b1829447ec',
        '0bc0dfed-639c-461b-865c-387cb9f3a165',
        '74ef0bf8-920e-4589-8e86-b045091d3210',
        'c60bb874-326f-4d36-a846-e15c84edd1f4',
        'b25a5bff-6299-4873-9b84-7f0f8cdb1fad',
        'ca83e61d-7301-42bd-8caf-e3b1a09ebf05',
        '2087cd0f-a54b-47fd-8a03-9022901e6fcd',
      ],
      'French European': [
        '79e637e9-896e-4837-92a6-852b9a2454be',
        'afa7576d-7035-406c-8a7d-8e55ce75388c',
        'f6465c32-634e-4147-9802-a4ba6198e1ee',
        '0c2e8edd-e304-4069-b175-f333f7e9fcfe',
        '4a8696e0-4cac-4a79-9849-2f6bd3eef327',
        'acea2755-6be3-4ffa-8327-a106dc7384c6',
        'bd46877a-cf7d-4e67-a548-5c1028d2b0fb',
        '2fb48995-1d2a-4926-bea9-5c1e4536ef0b',
        'f27859ad-680b-4ef0-a231-890ca45fc0e1',
        '8cbe4d0a-8b68-4b41-8079-ed3fc927f82e',
        '3ac14335-bd2f-4b35-a1e2-7e77ed44c0ce',
        '509886e8-7d5e-4434-a33f-9173b684c918',
        'cf7e311d-1365-41a4-aeea-5559322ab20a',
        'eac88806-28bc-46c4-bb6c-52b573e8520a',
        '565f4a72-1870-4e8c-bbeb-ebdebbc46f16',
        '5665234e-0393-4fa5-babf-02b59759a351',
        'f997baf1-1099-4ebf-a550-ad1c098b1791',
        '2dded436-c852-401d-8ae9-8441e524f900',
        '4c5c8625-37cb-42e4-8f08-e4bb13173aaf',
        '1958be19-7010-4b03-b9ee-3d2b578d2ba8',
        'e12ab406-92e9-4a8c-9d26-dd2a738da01e',
        'f1662617-510b-471d-b971-34d89498343e',
        'a1d7db01-6a60-4f45-8f42-0cdc9e83a6d1',
        'f822addf-d8ff-4052-8052-393b98867969',
        '0b7ecc32-d8ed-4c27-a732-9851b49a2099',
        '2bb27df8-29ba-4dae-9768-be98c2519728',
        '13be67b3-dd36-41b9-8577-1c302c5444bf',
        '7c23e6b4-d5b6-45ba-8c04-021259e27d1b',
        '873b6dc4-e5b5-48cb-99d2-63fa84f5e552',
        '2a84f540-c6a9-46f1-bb2f-44488dceee7c',
        '431036f1-3f52-4ebd-a709-f17942978174',
        '30157ff9-e1d2-4deb-9e9c-34234cb7aec2',
        '15b85a9e-3661-40c8-96be-beaeb1e8f86c',
        '7f8a7388-c9c6-4ff3-8656-192e1550e885',
        '2094df91-2e64-4e5f-b04f-8f387af482fa',
        '18b8e1fa-e6e2-4027-b155-e70766f8f29a',
        '33b53c97-7be8-4a7d-8ee5-439861ad91fe',
        '94a01e22-2690-401b-bf08-2f1c0fb20ad1',
        'd7b4dea4-3957-4d26-b691-9b56c1fe45aa',
        'de9c1e87-ac9d-4df1-8e8f-88e8b841a5fb',
        'eac3c1b7-8525-4cb4-aeeb-33faee03c89c',
        '420402df-226c-4c13-8c45-f44dd6eca6a5',
        '7e127254-e2df-4411-8cc9-ad2c3e27e313',
        '68170c90-5a0d-4a91-9cf5-b4ab9579b7c5',
        '311673bb-a6e5-4678-a053-bc5997c0ce3e',
        '62b71949-d83c-4276-9fa2-43fa44cc3600',
        'b3cb9772-5169-4dab-98d5-f50bd7381e01',
        '63e7c92c-3f5e-4261-81bb-97a091f421d1',
        '8f393846-5f2d-4064-a46d-c2088f225548',
        '95222bc7-e9ad-4e77-af22-0ddf4d4e0db3',
        '3e8771eb-513a-43dd-aa80-3f91b8fc9f31',
        'f66be369-4e36-44c2-b8e8-707453bd2839',
      ],
      Farmhouse: [
        '79e637e9-896e-4837-92a6-852b9a2454be',
        'afa7576d-7035-406c-8a7d-8e55ce75388c',
        '5c03175e-243a-43f3-bbef-8e230df1f307',
        'bd46877a-cf7d-4e67-a548-5c1028d2b0fb',
        '830c1e0a-a2f0-4419-b5c0-44b8a3cb7be0',
        'f6f61d69-56dd-45d6-a92c-640f916763b8',
        '2fb48995-1d2a-4926-bea9-5c1e4536ef0b',
        'c70d76fd-1a6e-4f80-9671-bf398b5a15fa',
        '9c063cfb-3ff8-41df-beb5-820f7fd46324',
        '6644b756-d227-47c1-ad79-c1902fe5d68d',
        'cdd058c3-e28e-4f4d-a111-56902c19cbf1',
        '44abd449-7add-4e96-a718-970518fffef1',
        'f997baf1-1099-4ebf-a550-ad1c098b1791',
        '2dded436-c852-401d-8ae9-8441e524f900',
        '4c5c8625-37cb-42e4-8f08-e4bb13173aaf',
        '1958be19-7010-4b03-b9ee-3d2b578d2ba8',
        'e12ab406-92e9-4a8c-9d26-dd2a738da01e',
        'f1662617-510b-471d-b971-34d89498343e',
        '80a5f7dd-87bb-48f0-8873-2ceb85639c62',
        'a1d7db01-6a60-4f45-8f42-0cdc9e83a6d1',
        'f822addf-d8ff-4052-8052-393b98867969',
        '0b7ecc32-d8ed-4c27-a732-9851b49a2099',
        '13be67b3-dd36-41b9-8577-1c302c5444bf',
        '15b85a9e-3661-40c8-96be-beaeb1e8f86c',
        'a75d64fb-c6b2-4a0a-be1a-7bd6e2971516',
        '159fddbb-df6c-447b-8ae0-9f9f7a7ee1b0',
        '5de3ffbd-9245-48d6-93fb-435ca13f8c1f',
        '0f27de8c-c6e6-44f9-98df-01ea137de933',
        'c8105714-4d01-4e5c-8259-36f9f6014550',
        '388330d8-e6f1-4a83-953d-9d9517cbaaa1',
        'b95de443-d59b-421d-8de8-eb45751a7add',
        '80a2a5ad-b09b-4708-8b98-e4e7056192e1',
        '38c8ab72-e815-4d5e-93e6-1fd38943b363',
        'eac3c1b7-8525-4cb4-aeeb-33faee03c89c',
        '420402df-226c-4c13-8c45-f44dd6eca6a5',
        '8883d5c2-8a9e-44c8-9831-225d91c84f03',
        '7e127254-e2df-4411-8cc9-ad2c3e27e313',
        '2ddc6fad-830a-4a4d-b666-f49869961146',
        'de37cef7-0903-47b0-89bf-a2ca2397766c',
        'f6db61b1-b3b2-4935-8140-73e38287512f',
        '050c99fd-024e-4bf3-a41f-8103149332ef',
        'f559ec74-6ad9-4069-b5bd-034dccab6c5a',
        '0d31ac35-4a16-45db-a15c-d72721731c11',
        '5148252a-f019-4126-a48d-fc20a03a3dda',
        'de59b393-13f0-4dd8-96f7-59aaed3ae416',
        '014708a7-2149-44f8-abc2-2baa578ebb65',
        '7ec3f9ac-a887-48ff-a509-b1d6ae7cbd2d',
        '5c772a56-433c-473d-82f5-6bef76424438',
        'ed5d55b0-530d-4d88-9e56-4f475ecabd1a',
        '9419cfde-98a8-4d12-b389-f6410a31274b',
        'e24ed235-f1b2-4428-a2de-e4ba530ae0a2',
        'a483b175-e2d2-441b-875e-7bea70d0e7ad',
        '9e156fd8-44d4-41d6-90e9-74487c9a4e3e',
        '95222bc7-e9ad-4e77-af22-0ddf4d4e0db3',
        '56e8517b-716e-4d74-8c8c-0140fc2b0a29',
        '1b4ea942-7663-4907-878a-d37ab1809d5b',
        'd72fa511-8b05-463d-8974-503ada9b9e33',
        'df7f1be4-275f-4cc7-8194-461b66f84859',
        '286472f5-0d0e-4402-ba3e-6b7746f0f941',
        'ebc0508b-3c7c-4e78-b5ef-83b2fd990853',
        '40473c4d-b269-4680-9151-bfe12508822c',
        'a9240385-4cc8-4530-afd1-adcbe1d45314',
        'd590f654-c290-4476-93f6-a9b1829447ec',
        '0bc0dfed-639c-461b-865c-387cb9f3a165',
        '74ef0bf8-920e-4589-8e86-b045091d3210',
        'c60bb874-326f-4d36-a846-e15c84edd1f4',
        'b25a5bff-6299-4873-9b84-7f0f8cdb1fad',
        'ca83e61d-7301-42bd-8caf-e3b1a09ebf05',
        '2087cd0f-a54b-47fd-8a03-9022901e6fcd',
      ],
      Craftsman: [
        'f6465c32-634e-4147-9802-a4ba6198e1ee',
        '830c1e0a-a2f0-4419-b5c0-44b8a3cb7be0',
        'f6f61d69-56dd-45d6-a92c-640f916763b8',
        'c70d76fd-1a6e-4f80-9671-bf398b5a15fa',
        '9c063cfb-3ff8-41df-beb5-820f7fd46324',
        'b67c2742-9b76-45d4-ae13-43357f1104a3',
        '3ac14335-bd2f-4b35-a1e2-7e77ed44c0ce',
        '509886e8-7d5e-4434-a33f-9173b684c918',
        '1271ffc5-5870-4fa1-afe2-7d632a3b9a75',
        'cf7e311d-1365-41a4-aeea-5559322ab20a',
        '565f4a72-1870-4e8c-bbeb-ebdebbc46f16',
        '5665234e-0393-4fa5-babf-02b59759a351',
        'ac0c33ec-2fd4-439a-85de-fd0cc3b20353',
        '2dded436-c852-401d-8ae9-8441e524f900',
        'f603abd9-3be3-4380-b8c1-b958e2ca3897',
        '7c23e6b4-d5b6-45ba-8c04-021259e27d1b',
        '873b6dc4-e5b5-48cb-99d2-63fa84f5e552',
        '2a84f540-c6a9-46f1-bb2f-44488dceee7c',
        '431036f1-3f52-4ebd-a709-f17942978174',
        '30157ff9-e1d2-4deb-9e9c-34234cb7aec2',
        '7f8a7388-c9c6-4ff3-8656-192e1550e885',
        'b2da217c-4f4e-4750-a7d7-df60cda4e8f2',
        '8883d5c2-8a9e-44c8-9831-225d91c84f03',
        '2ddc6fad-830a-4a4d-b666-f49869961146',
        'de37cef7-0903-47b0-89bf-a2ca2397766c',
        'f6db61b1-b3b2-4935-8140-73e38287512f',
        '050c99fd-024e-4bf3-a41f-8103149332ef',
        'f559ec74-6ad9-4069-b5bd-034dccab6c5a',
        '0d31ac35-4a16-45db-a15c-d72721731c11',
        '5148252a-f019-4126-a48d-fc20a03a3dda',
        'de59b393-13f0-4dd8-96f7-59aaed3ae416',
        '014708a7-2149-44f8-abc2-2baa578ebb65',
        '7ec3f9ac-a887-48ff-a509-b1d6ae7cbd2d',
        '5c772a56-433c-473d-82f5-6bef76424438',
        'ed5d55b0-530d-4d88-9e56-4f475ecabd1a',
        '9419cfde-98a8-4d12-b389-f6410a31274b',
        'e24ed235-f1b2-4428-a2de-e4ba530ae0a2',
        'a483b175-e2d2-441b-875e-7bea70d0e7ad',
        '9e156fd8-44d4-41d6-90e9-74487c9a4e3e',
        '3e8771eb-513a-43dd-aa80-3f91b8fc9f31',
        '56e8517b-716e-4d74-8c8c-0140fc2b0a29',
        '1b4ea942-7663-4907-878a-d37ab1809d5b',
        'd72fa511-8b05-463d-8974-503ada9b9e33',
        'df7f1be4-275f-4cc7-8194-461b66f84859',
        '286472f5-0d0e-4402-ba3e-6b7746f0f941',
        'ebc0508b-3c7c-4e78-b5ef-83b2fd990853',
        '40473c4d-b269-4680-9151-bfe12508822c',
        'a9240385-4cc8-4530-afd1-adcbe1d45314',
        'd590f654-c290-4476-93f6-a9b1829447ec',
        '0bc0dfed-639c-461b-865c-387cb9f3a165',
        '74ef0bf8-920e-4589-8e86-b045091d3210',
        'c60bb874-326f-4d36-a846-e15c84edd1f4',
        'b25a5bff-6299-4873-9b84-7f0f8cdb1fad',
        'ca83e61d-7301-42bd-8caf-e3b1a09ebf05',
        '2087cd0f-a54b-47fd-8a03-9022901e6fcd',
      ],
      Monochromatic: [
        '5c03175e-243a-43f3-bbef-8e230df1f307',
        '6644b756-d227-47c1-ad79-c1902fe5d68d',
        'cdd058c3-e28e-4f4d-a111-56902c19cbf1',
        'c788dc38-6647-4601-899d-bfdf5c0dcfb9',
        '8cbe4d0a-8b68-4b41-8079-ed3fc927f82e',
        '44abd449-7add-4e96-a718-970518fffef1',
        'fd529282-8a13-44de-a210-45c3fa28b45c',
        '15b85a9e-3661-40c8-96be-beaeb1e8f86c',
        'f300902e-c04d-45f5-8fa0-e83470e9e48d',
        '9974d8bf-7750-4348-a60b-1c9d0ea718d5',
        'fa5550d9-b2c3-42e4-95b1-ff6388e772b5',
        'b2da217c-4f4e-4750-a7d7-df60cda4e8f2',
        '2094df91-2e64-4e5f-b04f-8f387af482fa',
        '18b8e1fa-e6e2-4027-b155-e70766f8f29a',
        '33b53c97-7be8-4a7d-8ee5-439861ad91fe',
        '94a01e22-2690-401b-bf08-2f1c0fb20ad1',
        'd7b4dea4-3957-4d26-b691-9b56c1fe45aa',
        '87f6ed50-e3b8-437e-9cbb-0af2fa596c78',
        'a75d64fb-c6b2-4a0a-be1a-7bd6e2971516',
        '159fddbb-df6c-447b-8ae0-9f9f7a7ee1b0',
        '5de3ffbd-9245-48d6-93fb-435ca13f8c1f',
        '0f27de8c-c6e6-44f9-98df-01ea137de933',
        'c8105714-4d01-4e5c-8259-36f9f6014550',
        '388330d8-e6f1-4a83-953d-9d9517cbaaa1',
        'b95de443-d59b-421d-8de8-eb45751a7add',
        '80a2a5ad-b09b-4708-8b98-e4e7056192e1',
        '38c8ab72-e815-4d5e-93e6-1fd38943b363',
        '9cd9e9a2-6410-4a17-a1c4-9c912cfced84',
      ],
    },
  },
};
